import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import api from '../api/apiClient';

// Simulated AsyncStorage for web
const AsyncStorage = {
  getItem: (key: string): Promise<string | null> => Promise.resolve(localStorage.getItem(key)),
  setItem: (key: string, value: string): Promise<void> => Promise.resolve(localStorage.setItem(key, value)),
  removeItem: (key: string): Promise<void> => Promise.resolve(localStorage.removeItem(key)),
};

// Define user type
interface User {
  _id: string;
  name: string;
  email: string;
  userType: 'client' | 'stylist' | 'admin';
  profilePhoto?: string;
  location?: {
    city?: string;
    state?: string;
    zipCode?: string;
    coordinates?: {
      type: string;
      coordinates: number[];
    };
  };
}

// Auth context state
interface AuthContextType {
  user: User | null;
  token: string | null;
  loading: boolean;
  error: string | null;
  login: (email: string, password: string, turnstileToken?: string) => Promise<void>;
  logout: () => void;
  register: (userData: any) => Promise<void>;
  isAuthenticated: boolean;
}

// Create context with default values
const AuthContext = createContext<AuthContextType>({
  user: null,
  token: null,
  loading: false,
  error: null,
  login: async () => {},
  logout: () => {},
  register: async () => {},
  isAuthenticated: false,
});

export const useAuth = () => useContext(AuthContext);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  // Load user from storage on mount
  useEffect(() => {
    const loadUserFromStorage = async () => {
      try {
        console.log('🚀 [AuthContext] Loading user from storage...');
        const storedToken = await AsyncStorage.getItem('token');

        if (storedToken) {
          console.log('📦 [AuthContext] Found stored token, fetching fresh profile...');
          setToken(storedToken);
          // Always fetch fresh user data instead of using stale localStorage
          const success = await checkCurrentUser();
          if (success) {
            console.log('✅ [AuthContext] User profile loaded successfully');
            // Wait for React to process state updates before setting loading to false
            await new Promise(resolve => setTimeout(resolve, 0));
          } else {
            console.log('❌ [AuthContext] Failed to load user profile');
          }
        } else {
          console.log('❌ [AuthContext] No stored token found');
        }
      } catch (err: any) {
        console.error('Error loading user from storage:', err);
        localStorage.removeItem('token');
        localStorage.removeItem('user');
      } finally {
        console.log('✅ [AuthContext] Setting loading to false');
        setLoading(false);
      }
    };

    loadUserFromStorage();
  }, []);

  // Validate token by fetching profile - returns true on success
  const checkCurrentUser = async (): Promise<boolean> => {
    try {
      console.log('🔄 [AuthContext] Fetching current user profile...');
      const response = await api.user.getProfile();
      console.log('✅ [AuthContext] Profile response:', response.data?.data);
      if (response.data?.data) {
        const userData = response.data.data;
        console.log('👤 [AuthContext] Setting user with location:', userData.location);
        setUser(userData);
        setIsAuthenticated(true);
        // Also update localStorage with fresh data
        localStorage.setItem('user', JSON.stringify(userData));
        return true;
      } else {
        throw new Error('Invalid user data');
      }
    } catch (err: any) {
      console.error('Error validating token:', err);
      setUser(null);
      setToken(null);
      setIsAuthenticated(false);
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      return false;
    }
  };

  // Login method
  const login = async (email: string, password: string, turnstileToken = '') => {
    setLoading(true);
    setError(null);

    try {
      const response = await api.auth.login({ email, password, turnstileToken });
      const { token: newToken, user: userData } = response.data.data;
      setToken(newToken);
      setUser(userData);
      setIsAuthenticated(true);
      localStorage.setItem('token', newToken);
      localStorage.setItem('user', JSON.stringify(userData));
      await AsyncStorage.setItem('token', newToken);
    } catch (err: any) {
      console.error('Login error:', err);
      const msg = err.response?.data?.message || 'Login failed. Please try again.';
      setError(msg);
      throw new Error(msg);
    } finally {
      setLoading(false);
    }
  };

  // Logout method
  const logout = () => {
    setUser(null);
    setToken(null);
    setIsAuthenticated(false);
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    AsyncStorage.removeItem('token');
    // Call API logout endpoint (optional)
    api.post('/auth/logout',{})
      .catch((err: any) => console.error('Logout API error:', err));
  };

  // Register method
  const register = async (userData: any) => {
    setLoading(true);
    setError(null);

    try {
      const response = await api.auth.register(userData);
      const { token: newToken, user: newUser } = response.data.data;
      setToken(newToken);
      setUser(newUser);
      setIsAuthenticated(true);
      localStorage.setItem('token', newToken);
      localStorage.setItem('user', JSON.stringify(newUser));
      await AsyncStorage.setItem('token', newToken);
    } catch (err: any) {
      console.error('Registration error:', err);
      const msg = err.response?.data?.message || 'Registration failed. Please try again.';
      setError(msg);
      throw new Error(msg);
    } finally {
      setLoading(false);
    }
  };

  const value: AuthContextType = {
    user,
    token,
    loading,
    error,
    login,
    logout,
    register,
    isAuthenticated,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export default AuthContext;
